// ┌─────────────────────────────────────────────────────────────────────────┐
// │ Loader del `code` dels programes del MODEL (task aliasprogram).           │
// └─────────────────────────────────────────────────────────────────────────┘
//
// Avalua el codi font del programa (tal com viu a BD) com a mòdul ES via Blob +
// import() dinàmic, i n'obté la FACTORIA. Contracte esperat (veure
// `programContext.js`):
//   export default function create(ctx) { return { id, appliesTo, run } }
//
// `loadRemoteProgram` retorna la factoria; `instantiateRemoteProgram` la crida
// amb un `ctx` i en valida la calculadora. Aquest segon és el path COMPLET del
// contracte: el que s'executarà quan el `code` del catàleg deixi de portar
// imports interns i llegeixi els seus recursos de `ctx`.
//
// ESTAT (F0): el `code` del catàleg encara porta imports interns (`@vft/...`,
// `import('vehicles.json')`) que NO resolen quan s'avalua com a mòdul solt →
// l'import del Blob (o l'execució) llança. Capturem l'error perquè el front el
// PINTI. Migrar el `code` a llegir de `ctx.deps`/`ctx.helpers` és la fase
// següent (F1); quan es faci, carregarà net i s'executarà des del model.
//
// `import()` d'una Blob URL és natiu del navegador; cal evitar que el bundler
// (webpack a SherpaCRM, vite en standalone) intenti resoldre la URL en build-time.

export async function loadRemoteProgram(program) {
  const code = program && program.code != null ? String(program.code) : '';
  if (!code.trim()) {
    throw new Error('El programa no té codi font.');
  }
  let url = null;
  try {
    const blob = new Blob([code], { type: 'text/javascript' });
    url = URL.createObjectURL(blob);
    const mod = await import(/* webpackIgnore: true */ /* @vite-ignore */ url);
    const factory = mod && (mod.default || mod.create);
    if (typeof factory !== 'function') {
      throw new Error(
        "El codi no exposa el contracte esperat: 'export default function create(ctx) { … }'.",
      );
    }
    return factory;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

// Carrega una dependència type=js (un mòdul JS qualsevol del catàleg) com a mòdul
// ES via Blob + import(), igual que el `code` d'un programa però SENSE exigir el
// contracte create(ctx): torna l'export per defecte si n'hi ha, si no el namespace
// del mòdul. Així una dependència js es carrega dinàmicament com els datasets json.
export async function importRemoteModule(code) {
  const src = code != null ? String(code) : '';
  if (!src.trim()) return null;
  let url = null;
  try {
    const blob = new Blob([src], { type: 'text/javascript' });
    url = URL.createObjectURL(blob);
    const mod = await import(/* webpackIgnore: true */ /* @vite-ignore */ url);
    return (mod && 'default' in mod) ? mod.default : mod;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

// Carrega el `code` i l'INSTANCIA amb el `ctx` donat, validant que retorni una
// calculadora amb `run`. Path complet del contracte create(ctx).
export async function instantiateRemoteProgram(program, ctx) {
  const factory = await loadRemoteProgram(program);
  const calculator = factory(ctx);
  if (!calculator || typeof calculator.run !== 'function') {
    throw new Error(
      'La calculadora retornada per create(ctx) no exposa run(outputRows, fields, config).',
    );
  }
  return calculator;
}
